/**
 * Role-Based Dashboard router (S05 entry point).
 *
 * Rules:
 *  - Customer Mode → render onboarding empty state for ALL roles.
 *  - Demo Mode → route to the persona-specific layout:
 *      CAIO  → CaioDashboard (hero, fully built)
 *      CEO   → CeoDashboard  (boardroom, fully built)
 *      Other → RoleComingSoon (stage indicator, CAIO fallback view link)
 */
import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import CaioDashboard from "./CaioDashboard";
import CeoDashboard from "./CeoDashboard";
import ExecViewerDashboard from "./ExecViewerDashboard";
import AuditorDashboard from "./AuditorDashboard";
import RoleHomeDashboard from "./RoleHomeDashboard";
import CustomerDashboardEmpty from "./CustomerDashboardEmpty";
import { Flywheel } from "@/components/brand/Flywheel";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();

  if (isCustomer) {
    return (
      <CustomerDashboardEmpty
        userName={user?.name?.split(" ")[0]}
        roleCode={user?.roleCode}
      />
    );
  }

  // Route by persona id (e.g. 'caio', 'ceo', 'execviewer'). Anything we
  // haven't built a dedicated layout for falls through to the
  // RoleComingSoon stub.
  const personaId = user?.id || user?.personaId || user?.roleCode?.toLowerCase();
  switch (personaId) {
    case "caio":
      return <CaioDashboard />;
    case "ceo":
      return <CeoDashboard />;
    case "execviewer":
      return <ExecViewerDashboard />;
    case "auditor":
      return <AuditorDashboard />;
    case "legal":
    case "cio":
    case "cro":
    case "itsec":
    case "dslead":
    case "buhead":
      return <RoleHomeDashboard roleId={personaId} />;
    default:
      return <RoleComingSoon roleCode={user?.roleCode} userName={user?.name} />;
  }
}

function RoleComingSoon({ roleCode, userName }) {
  const { switchRole } = useAuth();
  const navigate = useNavigate();

  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up"
      data-testid="dashboard-coming-soon"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>

      <div className="relative max-w-2xl">
        <p className="eyebrow">{roleCode} view · Wave 2 in progress</p>
        <h1 className="text-display-md text-ink-primary mt-1">
          {userName?.split(" ")[0] ? `Hello, ${userName.split(" ")[0]}.` : "Hello."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl">
          The dedicated <span className="font-semibold text-ink-primary">{roleCode}</span>{" "}
          dashboard is on the next iteration. In the meantime, see the CAIO view
          for the full ALGOS narrative — same data, executive lens.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              switchRole("caio");
              navigate("/dashboard");
            }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700 transition-colors"
            data-testid="dashboard-view-as-caio"
          >
            <Sparkles size={14} /> View as CAIO
            <ArrowRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => {
              switchRole("ceo");
              navigate("/dashboard");
            }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-raised border border-border-strong text-sm font-semibold text-ink-primary hover:bg-inset transition-colors"
            data-testid="dashboard-view-as-ceo"
          >
            View as CEO
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
